/* eslint-disable functional/immutable-data */

import { useState, useCallback, useRef } from 'react';
import {
  runOnJS,
  useAnimatedGestureHandler,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import {
  logger,
  HapticFeedbackStyle,
  HapticNotificationFeedbackStyle,
  triggerHapticFeedback,
  triggerHapticNotificationFeedback,
} from '../../../../utils';
import type {
  UseCardGesturesProps,
  SwipeSnapPoint,
  GestureHandlerProps,
  SetNewSwipeDistanceProps,
  AnimatedGestureHandlerContext,
} from './useCardGestures.types';
import { SwipeStatus } from './useCardGestures.types';

/**
 * Custom hook for handling "swipe from left" interactions on the card component.
 *
 * It returns 'handleGestureEvent' handler for <PanGestureHandler> component and corresponding state.
 */
export const useCardGestures = ({
  snapPoints,
  minSwipeDistance = 0,
  maxSwipeDistance = Number.POSITIVE_INFINITY,
  onSwipeEnd,
  callback,
}: UseCardGesturesProps) => {
  // 💡 assign callback and snap points to the refs to prevent unnecessary renders
  const callbackRef = useRef(callback);
  const snapPointsRef = useRef(snapPoints);

  // ------------------------------------------------------------
  // ---- swiping state
  // ------------------------------------------------------------
  const [status, setStatus] = useState(SwipeStatus.Idle);

  const swipeDistance = useSharedValue(0);

  // ------------------------------------------------------------
  // ---- helpers
  // ------------------------------------------------------------

  /**
   * Clamps distance if it's smaller than the minimal value and higher than the maximal value.
   */
  const clampSwipeDistance = useCallback(
    (distance: number) => {
      'worklet';

      const clampedDistanceMin =
        distance <= minSwipeDistance ? minSwipeDistance : distance;
      const clampedDistanceMax =
        clampedDistanceMin >= maxSwipeDistance
          ? maxSwipeDistance
          : clampedDistanceMin;

      return clampedDistanceMax;
    },
    [maxSwipeDistance, minSwipeDistance],
  );

  /**
   * Returns corresponding snap point based on the provided swipe distance (will use first snap point as a fallback).
   */
  const findTargetSnapPoint = useCallback((distance: number) => {
    'worklet';

    return (
      snapPointsRef.current.find(({ thresholds }) => {
        return distance >= thresholds.min && distance <= thresholds.max;
      }) ?? snapPointsRef.current[0]
    );
  }, []);

  /**
   * Sets new swipe distance value and status.
   * 💡 Status will be updated on transition end.
   */
  const setNewSwipeDistanceAndStatus = useCallback(
    ({
      distance,
      smooth = false,
      newStatus = SwipeStatus.Idle,
    }: SetNewSwipeDistanceProps) => {
      'worklet';

      const limitedDistance = clampSwipeDistance(distance);
      const updateStatus = (animationCompleted = true) => {
        'worklet';

        if (!animationCompleted) return;
        runOnJS(setStatus)(newStatus);
      };

      // update swipe distance with timing
      // in this case onTransitionEnd action should be called after the timing.
      if (smooth) {
        swipeDistance.value = withTiming(limitedDistance, {}, updateStatus);
      }

      // ...or without smoothing (onTransitionEnd action will be called immediately)
      else {
        swipeDistance.value = limitedDistance;
        updateStatus();
      }
    },
    [swipeDistance, clampSwipeDistance],
  );

  /**
   * Triggers the provided callback + sets loading/busy state with corresponding haptic feedback.
   */
  const triggerCallback = useCallback(() => {
    const cb = callbackRef.current;

    if (!cb) return;

    // run callback and trigger the corresponding haptic feedback based on the response
    (async () => {
      setStatus(SwipeStatus.Busy);
      await triggerHapticFeedback(HapticFeedbackStyle.Medium);

      try {
        await cb();
        await triggerHapticNotificationFeedback(
          HapticNotificationFeedbackStyle.Success,
        );
      } catch (error: unknown) {
        logger.error(error);
        await triggerHapticNotificationFeedback(
          HapticNotificationFeedbackStyle.Error,
        );
      }

      // reset the card's initial location and status after the callback.
      setNewSwipeDistanceAndStatus({
        distance: 0,
        smooth: true,
        newStatus: SwipeStatus.Idle,
      });
    })();
  }, [setNewSwipeDistanceAndStatus]);

  /**
   * Triggers light haptic feedback.
   */
  const triggerSwipeHapticFeedback = useCallback(() => {
    (async () => {
      await triggerHapticFeedback(HapticFeedbackStyle.Light);
    })();
  }, []);

  /**
   * Sets a new active snap point and fires corresponding callbacks.
   */
  const setNewSnapPoint = useCallback(
    (targetSnapPoint: SwipeSnapPoint) => {
      'worklet';

      const { width: distance, shouldTriggerCallback } = targetSnapPoint;

      const isInitialSnapPoint = distance === 0;
      const newStatus = isInitialSnapPoint
        ? SwipeStatus.Idle
        : SwipeStatus.Expanded;

      setNewSwipeDistanceAndStatus({
        distance,
        smooth: !shouldTriggerCallback,
        newStatus,
      });

      // --------------------------------------
      // ---- optionally trigger the callback
      // --------------------------------------
      if (shouldTriggerCallback) {
        runOnJS(triggerCallback)();
      }
    },
    [setNewSwipeDistanceAndStatus, triggerCallback],
  );

  /**
   * Tracks user last known swipe distance for correct calculations.
   */
  const handleSwipeStart = (
    _: GestureHandlerProps,
    ctx: AnimatedGestureHandlerContext,
  ) => {
    'worklet';

    ctx.offsetX = swipeDistance.value;
  };

  /**
   * Sets translation based on the swiped distance.
   *
   * 💡 Clamps values from left and right to prevent unwanted transitions.
   */
  const handleActiveSwiping = (
    { translationX }: GestureHandlerProps,
    { offsetX = 0 }: AnimatedGestureHandlerContext,
  ) => {
    'worklet';

    // eslint-disable-next-line no-constant-binary-expression
    const shouldDisableSwiping = !callback ?? status === SwipeStatus.Busy;

    if (shouldDisableSwiping) return;

    const wasIdle = status === SwipeStatus.Idle;
    const hasStartedSwipingLeft = translationX < 0;
    const shouldConsiderSwipeStarted = wasIdle && hasStartedSwipingLeft;

    // trigger the haptic feedback on every new swipe interaction
    if (shouldConsiderSwipeStarted) {
      runOnJS(triggerSwipeHapticFeedback)();
    }

    setNewSwipeDistanceAndStatus({
      distance: offsetX + translationX,
      newStatus: SwipeStatus.Expanding,
    });
  };

  /**
   * Fires different actions based on the snap points.
   */
  const handleSwipeFinish = (
    { translationX }: GestureHandlerProps,
    { offsetX = 0 }: AnimatedGestureHandlerContext,
  ) => {
    'worklet';

    const targetSnapPoint = findTargetSnapPoint(offsetX + translationX);

    setNewSnapPoint(targetSnapPoint);
  };

  /**
   * Calls the external `onSwipeEnd` handler when the swipe is to the right direction.
   */
  const handleSwipeEnd = ({ translationX }: GestureHandlerProps) => {
    'worklet';

    if (translationX < 0 && typeof onSwipeEnd === 'function') {
      runOnJS(onSwipeEnd)();
    }
  };

  /**
   * Creates "onGestureEvent" handler for <PanGestureHandler /> component with corresponding callbacks.
   */
  const handleGestureEvent = useAnimatedGestureHandler({
    onStart: handleSwipeStart,
    onActive: handleActiveSwiping,
    onFinish: handleSwipeFinish,
    onEnd: handleSwipeEnd,
  });

  // ------------------------------------------------------------
  return {
    handleGestureEvent,
    triggerCallback,
    status,
    swipeDistance, // for animated styles
  };
};
